import { useContext, useState } from 'react';

import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
} from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import { Label } from '@repo/videa-components/src/ui/label';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { endpoints } from '@/react-query/endpoints';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { useGetTrialStatus, useUpdateTrialStatus } from '../hooks';
import type { TrialStatusData } from '../types';
import { TrialStatus } from '../types';

import { UpgradeImage } from './UpgradeImage';
import { UpgradeSuccess } from './UpgradeSuccess';

const UpgradeFeature = ({
  description,
  title,
  imagePath,
}: {
  description: string;
  title: string;
  imagePath: string;
}) => (
  <div className="vai-flex vai-flex-col vai-gap-3">
    <UpgradeImage className="vai-h-60" imagePath={imagePath} />
    <p className="vai-font-inter vai-font-semibold vai-text-zinc-900">
      {title}
    </p>
    <p className="vai-font-inter vai-text-sm vai-text-zinc-800">
      {description}
    </p>
  </div>
);

interface Props {
  trialStatusData?: TrialStatusData;
}

export const Upgrade = ({ trialStatusData }: Props) => {
  const [isChecked, setIsChecked] = useState(false);
  const [userName, setUserName] = useState('');
  const externalPatientContext = useContext(ExternalPatientContext);
  const queryClient = useQueryClient();
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const {
    mutateAsync: updateTrialStatus,
    isLoading,
    isError,
    isSuccess,
  } = useUpdateTrialStatus();
  const isTrialExpired =
    trialStatusData?.status === TrialStatus.TRIAL_EXPIRED ||
    trialStatusData?.status === TrialStatus.CANCEL_REQUEST_PAID;
  const isCentralizedBilling = trialStatusData?.centralizedBilling;

  const handleUpgrade = async () => {
    // We may be upgrading from either TRIAL_EXPIRED or CANCEL_REQUEST_TRIAL
    trackEvent(mixpanelEvents.externalUpgradeClick, {
      ...externalPatientContext,
    });
    await updateTrialStatus({
      status: TrialStatus.PAID_BUT_PROVISIONAL,
      metadata: { practiceUpgradedBy: userName.trim() },
    });
    queryClient.invalidateQueries({
      queryKey: [
        endpoints.reverseTrial,
        primaryOrganizationId,
        primaryPracticeId,
        endpoints.status,
      ],
    });
  };

  if (
    isSuccess ||
    trialStatusData?.status === TrialStatus.PAID_BUT_PROVISIONAL
  ) {
    return <UpgradeSuccess isCentralizedBilling={isCentralizedBilling} />;
  }

  const isButtonDisabled =
    (!isCentralizedBilling && !isChecked) || !userName.trim() || isLoading;

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-gap-6">
      <img
        alt="Branded shape pattern"
        className="vai-absolute vai-top-11 vai-right-0"
        src="/assets/images/external/branded-upgrade.svg"
      />

      <div className="vai-z-10">
        <p className="vai-font-merriweather vai-font-bold vai-text-3xl vai-text-navy-500">
          {isTrialExpired ? (
            <>
              {isCentralizedBilling ? (
                <Trans>
                  Want to continue using Detect AI? Request an upgrade
                </Trans>
              ) : (
                <Trans>
                  Get Detect AI for all general dentistry for just $399 /mo
                </Trans>
              )}
            </>
          ) : (
            <>
              {isCentralizedBilling ? (
                <Trans>
                  Want to keep Detect AI after the trial ends? Request an
                  upgrade
                </Trans>
              ) : (
                <Trans>
                  Get Detect AI for all general dentistry for just $299 /mo
                </Trans>
              )}
            </>
          )}
        </p>
        <p className="vai-mt-3 vai-font-inter vai-text-lg vai-text-navy-500">
          <Trans>
            VideaHealth brings Detect AI to the dental chair, helping clinicians
            provide better care, deepen patient relationships, and accelerate
            practice performance.
          </Trans>
        </p>
      </div>

      <div className="vai-grid vai-grid-cols-3 vai-gap-8 vai-z-10">
        <UpgradeFeature
          description={t`Access to 32 FDA-cleared AI models that detect the majority of restorative, periodontal and endodontic suspected diseases.`}
          title={t`Complete clinical AI for general dentistry`}
          imagePath="assets/images/external/clinical-view-image.png"
        />
        <UpgradeFeature
          description={t`Let the visualization help educate patients to drive case acceptance.`}
          title={t`Human-friendly visualization for patient education`}
          imagePath="assets/images/external/patient-view-image.png"
        />
        <UpgradeFeature
          description={t`Additional AI clinical context helps understand the severity of the suspected disease.`}
          title={t`AI clinical context to help speed up diagnosis and understanding`}
          imagePath="assets/images/external/visit-sidebar.png"
        />
      </div>

      {!isCentralizedBilling && (
        <div className="vai-flex vai-items-center vai-gap-2 vai-z-10">
          <Checkbox
            checked={isChecked}
            onCheckedChange={(checked) => {
              setIsChecked(typeof checked === 'boolean' ? checked : false);
            }}
            id="terms-and-conditions"
          />
          <Label
            className="vai-font-inter vai-font-medium vai-text-sm vai-text-zinc-800"
            htmlFor="terms-and-conditions"
          >
            <Trans>
              By checking this box, you agree that you are authorized to
              purchase this upgraded solution, and you agree to these{' '}
              <a
                className="vai-text-blue-500 vai-underline"
                href="https://hsps.widen.net/s/rj8hgnltws/eservices---hs1-terms-and-conditions"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackEvent(mixpanelEvents.externalTermsAndConditionsClick, {
                    ...externalPatientContext,
                    source: 'UPGRADE',
                  });
                }}
              >
                terms and conditions
              </a>
              . This service has a 12-month term and will automatically renew at
              the end of the initial 12-month term period.
            </Trans>
          </Label>
        </div>
      )}

      {isCentralizedBilling && (
        <p className="vai-font-inter vai-font-medium vai-text-sm vai-text-zinc-800 vai-z-10">
          <Trans>
            Your billing for Henry Schein One products is managed by another
            account within your dental organization. As a result, you cannot
            upgrade to the full Detect AI experience directly, but you can
            request an upgrade by clicking the button below. Your account owner
            will be notified of your request.
          </Trans>
        </p>
      )}

      <Input
        className="!vai-w-96 vai-z-10 !vai-text-sm"
        onChange={(e) => setUserName(e.target.value)}
        placeholder={
          isCentralizedBilling
            ? t`Enter your first and last name to request the upgrade`
            : t`Enter your first and last name to complete the upgrade`
        }
        type="text"
        value={userName}
      />

      <Button onClick={handleUpgrade} disabled={isButtonDisabled}>
        {isLoading && (
          <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-2 vai-border-t-transparent" />
        )}
        {isCentralizedBilling ? (
          <Trans>Request Upgrade</Trans>
        ) : (
          <Trans>Upgrade Now</Trans>
        )}
      </Button>
      {isError && (
        <Alert variant="destructive">
          <FontAwesomeIcon icon={faCircleInfo} />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {isCentralizedBilling ? (
              <Trans>
                Something went wrong when requesting the upgrade. Please try
                again.
              </Trans>
            ) : (
              <Trans>
                Something went wrong when upgrading your experience. Please try
                again.
              </Trans>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
